import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2, Package, ChefHat } from "lucide-react";
import { useProductIngredients, type ProductIngredient } from "@/hooks/useProductIngredients";
import { useIngredients } from "@/hooks/useIngredients";
import { usePricingCalculations } from "@/hooks/usePricingCalculations";
import {
  PricingStatusBadge,
  CostBreakdownCard,
  PricingCalculatorCard,
  OperationalCostsCard,
} from "@/components/products/pricing";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

interface ProductRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
}

export function ProductRecipeDialog({
  open,
  onOpenChange,
  product,
}: ProductRecipeDialogProps) {
  const { ingredients } = useIngredients();
  const {
    productIngredients,
    isLoading,
    fetchProductIngredients,
    addProductIngredient,
    updateProductIngredient,
    removeProductIngredient,
    calculateRecipeCost,
  } = useProductIngredients();

  const [selectedIngredient, setSelectedIngredient] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("1");
  const [isAdding, setIsAdding] = useState(false);
  const [targetMargin, setTargetMargin] = useState<number>(65);

  useEffect(() => {
    if (open && product) {
      fetchProductIngredients(product.id);
      setTargetMargin((product as any).target_margin_percentage ?? 65);
    }
  }, [open, product, fetchProductIngredients]);

  const handleAddIngredient = async () => {
    if (!product || !selectedIngredient || !quantity) return;

    setIsAdding(true);
    await addProductIngredient(product.id, selectedIngredient, parseFloat(quantity));
    setSelectedIngredient("");
    setQuantity("1");
    setIsAdding(false);
  };

  const handleUpdateQuantity = async (item: ProductIngredient, newQuantity: string) => {
    const qty = parseFloat(newQuantity);
    if (isNaN(qty) || qty <= 0) return;
    await updateProductIngredient(item.id, qty);
  };

  const handleRemove = async (id: string) => {
    await removeProductIngredient(id);
  };

  const handleTargetMarginChange = async (newMargin: number) => {
    setTargetMargin(newMargin);
    if (product) {
      await supabase
        .from("products")
        .update({ target_margin_percentage: newMargin } as any)
        .eq("id", product.id);
    }
  };

  const availableIngredients = ingredients.filter(
    (ing) => !productIngredients.some((pi) => pi.ingredient_id === ing.id)
  );

  const recipeCost = calculateRecipeCost();
  const productPrice = Number(product?.price) || 0;
  const pricing = usePricingCalculations(recipeCost, productPrice, targetMargin);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            Receita: {product?.name}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[calc(90vh-10rem)] -mx-6 px-6 overflow-y-auto">
          <div className="space-y-3 pb-4">
            <PricingStatusBadge
              status={pricing.marginStatus}
              realMargin={pricing.realMarginPercent}
              targetMargin={targetMargin}
            />

            <CostBreakdownCard
              ingredientCost={pricing.ingredientCost}
              cmvValue={pricing.cmvValue}
              cmvPercentage={pricing.cmvPercentage}
            />

            <PricingCalculatorCard
              targetMarginPercent={targetMargin}
              onTargetMarginChange={handleTargetMarginChange}
              currentPrice={pricing.currentPrice}
              suggestedPrice={pricing.suggestedPrice}
              realMarginPercent={pricing.realMarginPercent}
              grossMargin={pricing.grossMargin}
              contributionMargin={pricing.contributionMargin}
            />

            <OperationalCostsCard
              avgCardFeePercent={pricing.avgCardFeePercent}
              avgFixedExpensePercent={pricing.avgFixedExpensePercent}
              currentPrice={pricing.currentPrice}
              fixedExpensePerUnit={pricing.fixedExpensePerUnit}
              estimatedOperationalCost={pricing.estimatedOperationalCost}
              estimatedProfit={pricing.estimatedProfit}
              estimatedProfitPercent={pricing.estimatedProfitPercent}
              isLoading={pricing.isLoadingAverages}
            />

            <div className="border-t my-4" />

            <div className="space-y-3">
              <h3 className="font-medium text-sm flex items-center gap-2">
                <Package className="h-4 w-4" />
                Ingredientes da Receita
              </h3>

              <div className="flex gap-2">
                <Select value={selectedIngredient} onValueChange={setSelectedIngredient}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione um ingrediente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableIngredients.length === 0 ? (
                      <SelectItem value="none" disabled>
                        Nenhum ingrediente disponível
                      </SelectItem>
                    ) : (
                      availableIngredients.map((ing) => (
                        <SelectItem key={ing.id} value={ing.id}>
                          {ing.name} ({ing.unit})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-24"
                  placeholder="Qtd"
                />
                <Button
                  size="icon"
                  onClick={handleAddIngredient}
                  disabled={!selectedIngredient || !quantity || isAdding}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  Carregando...
                </div>
              ) : productIngredients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                  <Package className="h-10 w-10 opacity-50" />
                  <p>Nenhum ingrediente na receita</p>
                  <p className="text-xs">Adicione ingredientes para calcular o custo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {productIngredients.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-card border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.ingredient?.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="secondary" className="text-xs">
                            {item.ingredient?.unit}
                          </Badge>
                          <span>
                            Custo: {formatCurrency((item.ingredient?.cost_per_unit || 0) * item.quantity)}
                          </span>
                        </div>
                      </div>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleUpdateQuantity(item, e.target.value)}
                        className="w-20 text-center"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRemove(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t -mx-6 px-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
